import { AfterViewInit, Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { AppState } from '../../../../../core/reducers';
import { currentAuthToken } from '../../../../../core/auth';
import { Form } from '../../models';
import { FormService } from '../../services';
import { FormUtility } from '../../utils';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-create-form-schema',
  templateUrl: './create-form-schema.component.html',
  styleUrls: ['./create-form-schema.component.scss'],
})
export class CreateFormSchemaComponent implements AfterViewInit, OnDestroy {
  schema: { components: any[] } = { components: [] };
  prevSchema: { components: any[] } = { components: [] };
  schemaReady = false;
  versions: number[];
  getFormAutocomplete = this.formService.findForms.bind(this.formService, {});
  @ViewChild('formAutocomplete') formAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('versionSelect') versionSelect: MatSelect;

  private token: string;
  private subscriptions = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { form: Form; viewMode: boolean },
    private dialogRef: MatDialogRef<CreateFormSchemaComponent>,
    private formService: FormService,
    private formUtility: FormUtility,
    private store: Store<AppState>,
  ) {
    this.versions =
      this.data.viewMode && this.data.form?.version
        ? Array(this.data.form.version + 1)
            .fill(0)
            .map((x, i) => i)
        : [];

    this.subscriptions.add(
      this.store.pipe(select(currentAuthToken), take(1)).subscribe((token) => {
        this.token = token;

        const parsed = this.data.form?.schema
          ? JSON.parse(this.data.form.schema)
          : { components: [] };

        this.prevSchema = this.schema = this.formUtility.applyRuntimePlaceholders(
          parsed,
          this.token,
        );
        this.schemaReady = true;
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.data.form?.id) {
      setTimeout(() => {
        this.formAutocomplete?.setValue(this.data.form);
        this.formAutocomplete?.updateValueAndValidity();
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getTitle(): string {
    return `FORMS_TEMPLATES_MODULE.FORM.SCHEMA.${
      this.data.viewMode
        ? 'VIEW_TITLE'
        : this.schema?.components?.length
        ? 'UPDATE_TITLE'
        : 'CREATE_TITLE'
    }`;
  }

  formatFormOption(form: Form): string {
    return form?.name || '';
  }

  handleFormChange(form: Form): void {
    this.versions = form?.version
      ? Array(form.version + 1)
          .fill(0)
          .map((x, i) => i)
      : [];
  }

  copySchema(event: Event): void {
    event.stopPropagation();

    if (!this.formAutocomplete?.value) {
      this.formAutocomplete?.recorderInput?.nativeElement.focus();

      return;
    }

    this.formService
      .getFormById(this.formAutocomplete?.value?.id, false, this.versionSelect.value)
      .pipe(take(1))
      .subscribe((res) => {
        const parsed = res.data?.schema ? JSON.parse(res.data.schema) : { components: [] };
        this.schemaReady = false;
        this.prevSchema = this.formUtility.applyRuntimePlaceholders(parsed, this.token);
        this.handleSchemaChange(this.prevSchema);
        setTimeout(() => (this.schemaReady = true));
      });
  }

  handleSchemaChange(form): void {
    this.schema = form;
  }

  handleVersionChange(version): void {
    this.formService
      .getFormById(this.data.form?.id, false, version)
      .pipe(take(1))
      .subscribe((res) => {
        const parsed = res.data?.schema ? JSON.parse(res.data.schema) : { components: [] };
        this.schemaReady = false;
        this.schema = this.formUtility.applyRuntimePlaceholders(parsed, this.token);
        setTimeout(() => (this.schemaReady = true));
      });
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close(this.formUtility.revertRuntimePlaceholders(this.prevSchema, this.token));
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close(this.formUtility.revertRuntimePlaceholders(this.schema, this.token));
  }
}
